import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { Joyride, EVENTS, STATUS } from 'react-joyride'
import type { Step, EventData, Controls } from 'react-joyride'
import { useAuth } from './AuthContext'
import { getPortalSteps } from '../tour/portalSteps'
import { recommendationSteps } from '../tour/recommendationSteps'
import { tourOptions, tourStyles } from '../tour/tourStyles'
import {
  hasSeenFirstLoginPrompt,
  markFirstLoginPromptSeen,
  markTourCompleted,
} from '../tour/tourStorage'

type TourName = 'portal' | 'recommendation'

interface TourContextType {
  startTour: (name: TourName) => void
  isRunning: boolean
  showFirstLoginPrompt: boolean
  handleFirstLoginStart: () => void
  handleFirstLoginDismiss: () => void
}

const TourContext = createContext<TourContextType>({
  startTour: () => {},
  isRunning: false,
  showFirstLoginPrompt: false,
  handleFirstLoginStart: () => {},
  handleFirstLoginDismiss: () => {},
})

export function useTour() {
  return useContext(TourContext)
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user, isAdmin, canManageUsers, isIntern, isOfficeSupport } = useAuth()
  const isConsultant = user?.role === 'consultant'
  const [run, setRun] = useState(false)
  const [steps, setSteps] = useState<Step[]>([])
  const [activeTour, setActiveTour] = useState<TourName | null>(null)
  const [showFirstLoginPrompt, setShowFirstLoginPrompt] = useState(false)

  // Check for first-login prompt on mount
  useEffect(() => {
    if (user?.id && !hasSeenFirstLoginPrompt(user.id)) {
      setShowFirstLoginPrompt(true)
    }
  }, [user?.id])

  const startTour = useCallback((name: TourName) => {
    let tourSteps: Step[]

    if (name === 'portal') {
      tourSteps = getPortalSteps({ isAdmin, canManageUsers, isIntern, isOfficeSupport, isConsultant })
    } else {
      tourSteps = recommendationSteps
    }

    setActiveTour(name)
    setSteps(tourSteps)
    setRun(true)
  }, [isAdmin, canManageUsers, isIntern, isOfficeSupport, isConsultant])

  const handleFirstLoginStart = useCallback(() => {
    if (user?.id) {
      markFirstLoginPromptSeen(user.id)
    }
    setShowFirstLoginPrompt(false)
    startTour('portal')
  }, [user?.id, startTour])

  const handleFirstLoginDismiss = useCallback(() => {
    if (user?.id) {
      markFirstLoginPromptSeen(user.id)
    }
    setShowFirstLoginPrompt(false)
  }, [user?.id])

  const handleEvent = useCallback((data: EventData, _controls: Controls) => {
    const { type, status } = data
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

    if (type === EVENTS.TOUR_END && finishedStatuses.includes(status)) {
      setRun(false)
      if (activeTour && user?.id) {
        markTourCompleted(user.id, activeTour)
      }
      setActiveTour(null)
    }
  }, [activeTour, user?.id])

  return (
    <TourContext.Provider
      value={{
        startTour,
        isRunning: run,
        showFirstLoginPrompt,
        handleFirstLoginStart,
        handleFirstLoginDismiss,
      }}
    >
      {children}
      <Joyride
        steps={steps}
        run={run}
        continuous
        options={{
          ...tourOptions,
          buttons: ['skip', 'back', 'close', 'primary'],
        }}
        styles={tourStyles}
        onEvent={handleEvent}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Done',
          next: 'Next',
          skip: 'Skip Tour',
        }}
      />
    </TourContext.Provider>
  )
}
